import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../styles/LoginModal.module.css';
import { loginUser, newLoginUser, fetchCaptcha, verifyCaptchaThunk } from '../store/userSlice';
import { verifyPassword } from '../api/user'; // 导入密码验证API
import AlertModal from './AlertModal';
import { useTranslation } from 'react-i18next';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('code');
  const [input, setInput] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.user);

  useEffect(() => {
    if (isOpen && isCodeSent && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsCodeSent(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOpen, isCodeSent, countdown]);

  // 重置表单
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setPassword('');
    }
  }, [activeTab, isOpen]);

  if (!isOpen) return null;

  // 判断输入是邮箱还是手机号
  const isPhoneNumber = (input) => /^\d{11}$/.test(input);
  const isEmail = (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);

  const handleSendCode = () => {
    if (!input) {
      setAlertMessage(t('login.pleaseEnterEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    if (!isPhoneNumber(input) && !isEmail(input)) {
      setAlertMessage(t('login.pleaseEnterValidEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    dispatch(fetchCaptcha(input)).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        setIsCodeSent(true);
        setCountdown(60);
        const apiMessage = result.payload && result.payload.message;
        setAlertMessage(apiMessage || t('login.codeSent')); // 使用API消息或默认消息
        setAlertOpen(true);
      } else {
        setAlertMessage(t('login.failedToSendCode'));
        setAlertOpen(true);
      }
    });
  };

  // 验证码登录提交处理
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!input) {
      setAlertMessage(t('login.pleaseEnterEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    if (!code) {
      setAlertMessage(t('login.pleaseEnterVerificationCode'));
      setAlertOpen(true);
      return;
    }

    const captchaResult = await dispatch(verifyCaptchaThunk({ phoneNumbers: input, smsCode: code }));
    if (captchaResult.meta.requestStatus === 'fulfilled') {
      const { code: responseCode, message, uid } = captchaResult.payload;
      if (responseCode === 1) {
        const loginResult = await dispatch(newLoginUser({ uid: uid || input }));
        if (loginResult.meta.requestStatus === 'fulfilled') {
          onLoginSuccess();
        } else {
          setAlertMessage(t('login.loginFailed'));
          setAlertOpen(true);
        }
      } else {
        setAlertMessage(message);
        setAlertOpen(true);
        setCode('');
      }
    } else {
      setAlertMessage(t('login.verificationFailed'));
      setAlertOpen(true);
      setCode('');
    }
  };

  // 账号密码登录提交处理 - 修改为使用verifyPassword API
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!input) {
      setAlertMessage(t('login.pleaseEnterEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    if (!password) {
      setAlertMessage(t('login.pleaseEnterPassword'));
      setAlertOpen(true);
      return;
    }

    try {
      // 使用verifyPassword API验证密码
      const response = await verifyPassword({ 
        phoneNumbers: input, 
        password: password // 这里使用password作为smsCode参数，根据API定义
      });

      // 检查响应结果
      if (response) {
        const { code: responseCode, message, uid } = response;
        console.log('Verify Password Response:', response.data); // 打印完整的响应数据

        console.log('Verify Password Response Code:', responseCode); // 打印响应码
        console.log('Verify Password Response Message:', message); // 打印响应消息
        console.log('Verify Password Response UID:', uid); // 打印响应UID
        if (responseCode === 1) {
          // 密码验证成功，继续登录流程
          const loginResult = await dispatch(newLoginUser({ uid: uid || input }));
          if (loginResult.meta.requestStatus === 'fulfilled') {
            onLoginSuccess();
          } else {
            setAlertMessage(t('login.loginFailed'));
            setAlertOpen(true);
          }
        } else {
          // 密码验证失败
          setAlertMessage(message || t('login.incorrectPassword'));
          setAlertOpen(true);
          setPassword('');
        }
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAlertMessage(t('login.loginError'));
      setAlertOpen(true);
      setPassword('');
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
  };

  return (
    <>
      <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className={styles.loginModal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <button className={styles.closeBtn} onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
            <div className={styles.logo}>
              <img
                src={`${process.env.PUBLIC_URL}/images/logo.jpg`}
                alt="Logo"
                className={styles.logoImage}
              />
              {t('header.logo')}
            </div>
            <h2>手机号账号 获取默认密码</h2>
            <p className={styles.modalDesc}>输入您的手机号，系统将通过验证码确认身份，并告知您的默认登录密码</p>
          </div>

          <div className={styles.modalContent}>
            <form onSubmit={handleCodeSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="input">手机号</label>
                <div className={styles.inputWithIcon}>
                  <i className="fas fa-mobile-alt"></i>
                  <input
                    type="text"
                    id="input"
                    className={styles.formControl}
                    placeholder="请输入您的手机号"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
              {error && <p className={styles.error}>{error}</p>}
              <button type="button" className={styles.btn} onClick={handleSendCode} disabled={isCodeSent || loading}>
                {loading ? '查询中...' : isCodeSent ? `默认密码已发送至手机（${countdown}s）` : '查询默认密码'}
              </button>
              <div className={styles.formFooter}>
                <p>点击后，默认密码将以短信形式发送到您的手机</p>
              </div>
            </form>
          </div>
        </div>
      </div>
      <AlertModal isOpen={alertOpen} message={alertMessage} onClose={handleAlertClose} />
    </>
  );
};

export default LoginModal;